interface PartnersSectionProps {
  countyName: string | null;
  countyLogo: string | null;
  countySubtitle: string;
  countyDescription: string;
  ctaText: string;
  dashboardUrl: string;
}

export default function PartnersSection({
  countyName,
  countyLogo,
  countySubtitle,
  countyDescription,
  ctaText,
  dashboardUrl,
}: PartnersSectionProps) {
  // Add "City" for Kisumu
  const displayCountyName = countyName === "Kisumu" ? "Kisumu City" : countyName;

  // Check if this is Kakamega landing page (uses different M-taka description)
  const isKakamega = countyName?.toLowerCase().includes("kakamega") || false;

  // M-taka description - different for Kakamega
  const mtakaDescription = isKakamega
    ? "M-taka is a tech integrated waste management and recycling social enterprise. Through a circular economy platform, M-taka digitizes and formalizes the waste recovery value chain in Africa. The platform's AI-powered dashboards generate real-time waste traceability data, supporting recyclers, corporates, and municipalities in meeting Extended Producer Responsibility (EPR) compliance and ESG reporting requirements. By integrating financial access, supply chain traceability, logistics coordination, and smart data analytics, M-taka creates a scalable and sustainable model for inclusive waste management and climate-resilient urban systems across Africa."
    : "is a revolutionary waste management system leveraging technology to transform waste collection, processing, and recycling. We empower communities through job creation and sustainable environmental practices.";

  return (
    <section id="partners" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Partners
            </h2>
            <p className="text-lg text-gray-600">
              Collaborating for sustainable waste management
            </p>
          </div>

          {/* Partner Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* County Partner */}
            {countyName && (
              <div className="bg-white rounded-lg p-8 shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                  {countyLogo ? (
                    <img
                      src={countyLogo}
                      alt={countyName}
                      width={64}
                      height={64}
                      className="w-16 h-16 object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {countyName[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {displayCountyName}
                    </h3>
                    <p className="text-sm text-gray-600">{countySubtitle}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {countyDescription}
                </p>
              </div>
            )}

            {/* M-Taka Partner */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src="/icon.png"
                  alt="M-Taka"
                  width={64}
                  height={64}
                  className="w-16 h-16 object-contain"
                />
                <div>
                  <h3 className="font-poppins text-2xl tracking-tighter">
                    <span className="font-extrabold text-primary">M</span>
                    <span className="font-bold">-taka</span>
                  </h3>
                  <p className="text-sm text-gray-600">
                    Kenyan Social Enterprise
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {isKakamega ? (
                  // Kakamega uses full description starting with M-taka
                  mtakaDescription
                ) : (
                  // Other counties use M-taka branding inline
                  <>
                    <span className="font-poppins tracking-tighter">
                      <span className="font-extrabold text-primary">M</span>
                      <span className="font-bold">-taka</span>
                    </span>{" "}
                    {mtakaDescription}
                  </>
                )}
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary to-primary-hover rounded-lg p-8 md:p-12 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {ctaText}
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Access real-time analytics and insights{displayCountyName && ` for ${displayCountyName}`}
            </p>
            <a
              href={dashboardUrl}
              className="inline-block px-8 py-3 bg-white text-primary rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-lg"
            >
              View Dashboard
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
